import {$, $$} from '@vue-macros/reactivity-transform/macros'
import {
  IRelationPropertyTypeInterfaces,
} from '@toolify/server/src/services/DatabasePropertyService/strategies/ColumnTypeStrategies/RelationPropertyTypeStrategy/types/IRelationPropertyTypeInterfaces'
import {PropertyStrategyComponentType} from '@toolify/client/src/modules/tableItems/enum/PropertyStrategyComponentType'
import {FilterPropertyOperator} from '@toolify/server/src/services/FilterService/enum/FilterPropertyOperator'
import {FilterItemType} from '@toolify/server/src/services/FilterService/enum/FilterItemType'
import {FilterPropertyPayloadType} from '@toolify/server/src/services/FilterService/enum/FilterPropertyPayloadType'
import {IDatabasePropertyResponseInstance} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IDatabasePropertyResponseInstance'
import {useParticularColors} from '@toolify/client/src/modules/color/composables/useParticularColors'
import {getParticularColorBySeed} from '@toolify/client/src/modules/color/composables/getParticularColorBySeed'
import {FilterGroupOperator} from '@toolify/server/src/services/FilterService/enum/FilterGroupOperator'
import {IFilter} from '@toolify/server/src/services/FilterService/types/IFilter'
import {DatabaseViewType} from '@toolify/server/src/models/mongoose/DatabaseViewModel/enum/DatabaseViewType'
import {isTimeAxisViewType} from '@toolify/client/src/modules/databases/functions/isTimeAxisViewType'
import {IReverseRelationPropertyTypeInterfaces} from '@toolify/server/src/services/DatabasePropertyService/strategies/ColumnTypeStrategies/ReverseRelationPropertyTypeStrategy/types/IReverseRelationPropertyTypeInterfaces'
import {GroupSortModeType} from '@toolify/server/src/services/DatabasePropertyService/enum/GroupSortModeType'
import {COMMON_GROUP_SORT_LABEL_KEYS} from '@toolify/client/src/modules/databases/utils/groupSortModes'

export function useRelationValueTypeStrategy() {
  const strategyComponentType = PropertyStrategyComponentType.Dropdown
  const {getColorByParticularColor} = $(useParticularColors())
  const fullValueToText = (value: IRelationPropertyTypeInterfaces['fullValue']) => {
    if(!value) {
      return ''
    }
    return value.firstItems?.map(item => item.text).join(', ')
  }

  const fullValueToColor = (value: IRelationPropertyTypeInterfaces['fullValue'], property?: IDatabasePropertyResponseInstance): {color: string, colorKey:string | null} | null => {
    if(!value || value?.count === 0) {
      return null
    }
    if(value.firstItems.length === 1) {
      const item = value.firstItems[0]
      const particularColor = getParticularColorBySeed(item.text)
      return {
        color: getColorByParticularColor(particularColor),
        colorKey: property ? `${property}-${item.text}` : null,
      }
    }
    const combinedItemNames = value.firstItems?.map(item => item.text).join(',')
    const particularColor = getParticularColorBySeed(combinedItemNames)
    return {
      color: getColorByParticularColor(particularColor),
      colorKey: property ? `${property}-${combinedItemNames}` : null,
    }
  }

  const storageValueToInputValue = (value: IRelationPropertyTypeInterfaces['storedValue']) => {
    return value?.firstItems.map(item => item.id) || []
  }

  const fullValueToInputValue = (value: IRelationPropertyTypeInterfaces['fullValue']) => {
    if(!value || !value.firstItems) {
      return []
    }
    return value.firstItems.map(item => item.id)
  }

  const getPossibleValueFilter = (possibleValue: IRelationPropertyTypeInterfaces['fullValue'], group: string) => {
    return {
      type: FilterItemType.Property,
      id: group,
      operator: {
        type: (possibleValue?.firstItems?.[0]?.id ?? null) ? FilterPropertyOperator.ArrayContains : FilterPropertyOperator.Equals,
        options: {
          subProperty: 'id',
        },
      },
      subProperty: 'firstItems',
      payload: {
        type: FilterPropertyPayloadType.Static,
        value: possibleValue?.firstItems?.[0]?.id ?? null,
      },
      isDisabled: false,
    }
  }

  const sortGroupItems = (a: {fullValue: IRelationPropertyTypeInterfaces['fullValue']}, b: {fullValue: IRelationPropertyTypeInterfaces['fullValue']}): number => {
    const relationA = a.fullValue
    const relationB = b.fullValue
    
    const hasA = relationA && relationA.firstItems && relationA.firstItems.length > 0
    const hasB = relationB && relationB.firstItems && relationB.firstItems.length > 0

    if (!hasA && !hasB) return 0
    if (!hasA) return 1
    if (!hasB) return -1
    
    const textA = (relationA?.firstItems || []).map(item => item.text).sort().join(', ')
    const textB = (relationB?.firstItems || []).map(item => item.text).sort().join(', ')
    
    return textA.localeCompare(textB)
  }

  const compareItemValues = (
    valueA: IRelationPropertyTypeInterfaces['fullValue'],
    valueB: IRelationPropertyTypeInterfaces['fullValue'],
    sortDirection: '1' | '-1',
  ): number => {
    const hasValueA = Array.isArray(valueA?.firstItems) && valueA.firstItems.length > 0
    const hasValueB = Array.isArray(valueB?.firstItems) && valueB.firstItems.length > 0

    if (!hasValueA && !hasValueB) return 0
    if (!hasValueA) return 1
    if (!hasValueB) return -1

    const textA = valueA.firstItems[0].text || ''
    const textB = valueB.firstItems[0].text || ''

    const comparison = textA.localeCompare(textB)

    return sortDirection === '1' ? comparison : -comparison
  }

  const updateValueBySingleValueGroupSwitch = (
    currentValue: IRelationPropertyTypeInterfaces['fullValue'],
    oldGroupValue: IRelationPropertyTypeInterfaces['fullValue'],
    newGroupValue: IRelationPropertyTypeInterfaces['fullValue'],
  ): string[] | null => {
    const newValue = currentValue?.firstItems?.map(e => e.id).filter(e => e !== oldGroupValue?.firstItems?.[0]?.id).concat(newGroupValue?.firstItems?.[0]?.id || null).filter(e => !!e) || []
    return newValue.length ? newValue : null
  }

  const updateFullValueBySingleValueGroupSwitch = (
    currentValue: IRelationPropertyTypeInterfaces['fullValue'],
    oldGroupValue: IRelationPropertyTypeInterfaces['fullValue'],
    newGroupValue: IRelationPropertyTypeInterfaces['fullValue'],
  ): IRelationPropertyTypeInterfaces['fullValue'] | null => {
    const newFirstItems = currentValue?.firstItems?.filter(e => e.id !== oldGroupValue?.firstItems?.[0]?.id).concat(newGroupValue?.firstItems?.[0] || null).filter(e => !!e) || []
    return newFirstItems.length ? {
      count: newFirstItems.length,
      firstItems: newFirstItems,
    } : null
  }

  const searchToFilterQuery = (search: string, databaseProperties: IDatabasePropertyResponseInstance[]): IFilter => {
    return {
      type: FilterItemType.Group,
      operator: FilterGroupOperator.Or,
      items: databaseProperties.map(databaseProperty => {
        return {
          type: FilterItemType.Property,
          id: databaseProperty.id,
          subProperty: 'firstItems',
          operator: {
            type: FilterPropertyOperator.ArrayContains,
            options: {
              subProperty: 'text',
            },
          },
          payload: {
            type: FilterPropertyPayloadType.Static,
            value: search,
          },
          isDisabled: false,
        }
      }),
    }
  }

  const canGroupByView = (viewType: DatabaseViewType, property: IDatabasePropertyResponseInstance) => {
    if (isTimeAxisViewType(viewType)) return true
    if (viewType !== DatabaseViewType.Table && viewType !== DatabaseViewType.Kanban) return false
    const relationOpts = property.valueTypeOptions as IRelationPropertyTypeInterfaces['options'] | undefined
    const reverseOpts = property.valueTypeOptions as IReverseRelationPropertyTypeInterfaces['options'] | undefined
    return Boolean(relationOpts?.onlyOne ?? reverseOpts?.onlyFirstItem)
  }

  const groupSort = {
    availableModes: [
      GroupSortModeType.Natural,
      GroupSortModeType.ValueAsc,
      GroupSortModeType.ValueDesc,
      GroupSortModeType.CountDesc,
      GroupSortModeType.CountAsc,
    ],
    defaultMode: GroupSortModeType.Natural,
    getLabelKey: (mode: GroupSortModeType): string => COMMON_GROUP_SORT_LABEL_KEYS[mode],
  }

  return $$({
    fullValueToText,
    fullValueToColor,
    storageValueToInputValue,
    fullValueToInputValue,
    getPossibleValueFilter,
    strategyComponentType,
    sortGroupItems,
    compareItemValues,
    updateValueBySingleValueGroupSwitch,
    updateFullValueBySingleValueGroupSwitch,
    searchToFilterQuery,
    canGroupByView,
    groupSort,
  })
}
